import { useState, useEffect } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Calculator, Download, CheckCircle, DollarSign, CreditCard, AlertTriangle, Banknote } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReconcileModal = ({ open, onClose, customer, onReconciled }) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [data, setData] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState({});

  useEffect(() => {
    if (open && customer) {
      fetchReconcileData();
      setSelectedInvoices({});
      setPaymentAmount("");
      setPaymentReference("");
    }
  }, [open, customer]);

  const fetchReconcileData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers/${customer.id}/reconcile`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching reconcile data:", error);
      toast.error("Failed to load reconciliation data");
    } finally {
      setLoading(false);
    }
  };

  // Toggle invoice selection for payment allocation
  const toggleInvoiceSelection = (invoice) => {
    if (invoice.balance <= 0) return; // Can't select paid invoices
    
    setSelectedInvoices(prev => {
      const newSelected = { ...prev };
      if (newSelected[invoice.id]) {
        delete newSelected[invoice.id];
      } else {
        newSelected[invoice.id] = invoice.balance; // Default to full balance
      }
      return newSelected;
    });
  };

  // Update allocation amount for a specific invoice
  const updateAllocation = (invoiceId, amount) => {
    const numAmount = parseFloat(amount) || 0;
    setSelectedInvoices(prev => ({
      ...prev,
      [invoiceId]: numAmount
    }));
  };

  // Calculate total selected
  const totalSelected = Object.values(selectedInvoices).reduce((sum, amt) => sum + amt, 0);

  // Pay selected invoices
  const handlePaySelected = async () => {
    const assignments = Object.entries(selectedInvoices)
      .filter(([_, amount]) => amount > 0)
      .map(([invoice_id, amount]) => ({
        invoice_id,
        amount
      }));

    if (assignments.length === 0) {
      toast.error("Please select invoices to pay");
      return;
    }

    setProcessing(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/customers/${customer.id}/reconcile`, {
        assignments,
        reference: paymentReference || `Payment ${new Date().toLocaleDateString()}`
      }));
      toast.success(`Payment of R ${totalSelected.toFixed(2)} allocated successfully`);
      fetchReconcileData();
      setSelectedInvoices({});
      setPaymentReference("");
      if (onReconciled) onReconciled();
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error(errorMessage(error, "Failed to process payment allocation"));
    } finally {
      setProcessing(false);
    }
  };

  // Quick payment - record payment and auto-allocate to oldest invoices
  const handleQuickPayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }

    // Auto-allocate to oldest unpaid invoices first
    const unpaidInvoices = (data?.invoices || [])
      .filter(inv => inv.doc_type === 'PURCHASE' && inv.balance > 0)
      .sort((a, b) => new Date(a.date) - new Date(b.date)); // Oldest first

    let remainingAmount = amount;
    const assignments = [];
    
    for (const inv of unpaidInvoices) {
      if (remainingAmount <= 0) break;
      const allocate = Math.min(inv.balance, remainingAmount);
      assignments.push({ invoice_id: inv.id, amount: allocate });
      remainingAmount -= allocate;
    }

    if (assignments.length === 0) {
      toast.error("No unpaid invoices to allocate payment to");
      return;
    }

    setProcessing(true);
    try {
      await saveWithRetry(() => axios.post(`${API}/customers/${customer.id}/reconcile`, {
        assignments,
        reference: paymentReference || `Payment ${new Date().toLocaleDateString()}`
      }));
      
      const allocated = amount - remainingAmount;
      if (remainingAmount > 0) {
        toast.success(`R ${allocated.toFixed(2)} allocated. R ${remainingAmount.toFixed(2)} remains as credit.`);
      } else {
        toast.success(`Payment of R ${allocated.toFixed(2)} allocated successfully`);
      }
      
      fetchReconcileData();
      setPaymentAmount("");
      setPaymentReference("");
      setSelectedInvoices({});
      if (onReconciled) onReconciled();
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error(errorMessage(error, "Failed to process payment"));
    } finally {
      setProcessing(false);
    }
  };

  const handleExportCSV = () => {
    if (!data || data.invoices.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["DocType", "Date", "InvNo", "InvTotal", "Balance", "Period", "AmtPaid", "Description"];
    const csvContent = [
      headers.join(","),
      ...data.invoices.map(inv => [
        inv.doc_type,
        inv.date,
        inv.inv_no,
        inv.inv_total.toFixed(2),
        inv.balance.toFixed(2),
        inv.period,
        inv.inv_amt_paid.toFixed(2),
        `"${inv.description}"`
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reconcile_${customer.name}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export completed");
  };

  const getPeriodStyle = (period) => {
    switch (period) {
      case 'CURRENT': return 'bg-green-100 text-green-700';
      case '30 DAYS': return 'bg-yellow-100 text-yellow-700';
      case '60 DAYS': return 'bg-orange-100 text-orange-700';
      case '90 DAYS': return 'bg-red-100 text-red-600';
      case '120+ DAYS': return 'bg-red-200 text-red-800 font-bold';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (!customer) return null;

  // Separate unpaid invoices from payments/credits
  // Include both PURCHASE and OPENING doc types as unpaid invoices
  const unpaidInvoices = (data?.invoices || []).filter(inv => 
    (inv.doc_type === 'PURCHASE' || inv.doc_type === 'OPENING') && inv.balance > 0
  );
  const paidInvoices = (data?.invoices || []).filter(inv => inv.doc_type === 'PURCHASE' && inv.balance === 0);
  const payments = (data?.invoices || []).filter(inv => inv.doc_type !== 'PURCHASE' && inv.doc_type !== 'OPENING');

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Calculator className="h-5 w-5" />
            Reconcile Account: {customer.name}
          </DialogTitle>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>Account: {customer.account_number}</span>
          </div>
        </DialogHeader>

        {/* Balance Summary Cards */}
        {data && (
          <div className="grid grid-cols-3 gap-3 mb-2">
            {/* Unpaid Invoices Total - Use total_unreconciled which already includes opening balance if unpaid */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-600 text-xs font-medium mb-1">
                <AlertTriangle className="h-4 w-4" />
                Unpaid Invoices
              </div>
              <div className="text-2xl font-bold text-red-700">
                R {(data.total_unreconciled || 0).toFixed(2)}
              </div>
            </div>
            
            {/* Available for Allocation */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-green-600 text-xs font-medium mb-1">
                <Banknote className="h-4 w-4" />
                Available for Allocation
              </div>
              <div className="text-2xl font-bold text-green-700">
                R {(data.available_for_allocation || 0).toFixed(2)}
              </div>
              <div className="text-xs text-green-600 mt-1">
                (Unassigned payments/credits)
              </div>
            </div>
            
            {/* Net Balance Owing - Use calculated_balance for accuracy */}
            <div className={`${(data.calculated_balance ?? data.balance_owing ?? 0) > 0 ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'} border rounded-lg p-3`}>
              <div className={`flex items-center gap-2 ${(data.calculated_balance ?? data.balance_owing ?? 0) > 0 ? 'text-orange-600' : 'text-blue-600'} text-xs font-medium mb-1`}>
                <Calculator className="h-4 w-4" />
                Net Balance {(data.calculated_balance ?? data.balance_owing ?? 0) >= 0 ? 'Owing' : 'Credit'}
              </div>
              <div className={`text-2xl font-bold ${(data.calculated_balance ?? data.balance_owing ?? 0) > 0 ? 'text-orange-700' : 'text-blue-700'}`}>
                R {(Math.abs(data.calculated_balance ?? data.balance_owing ?? 0) || 0).toFixed(2)}
              </div>
              <div className={`text-xs ${(data.calculated_balance ?? data.balance_owing ?? 0) > 0 ? 'text-orange-600' : 'text-blue-600'} mt-1`}>
                (Actual balance after all transactions)
              </div>
            </div>
          </div>
        )}

        {/* Quick Payment Section */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-5 w-5 text-green-600" />
            <Label className="text-lg font-semibold text-green-800">Record Payment</Label>
          </div>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-sm text-green-700">Payment Amount (R)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="bg-white text-lg font-semibold"
                data-testid="payment-amount-input"
              />
            </div>
            <div className="flex-1">
              <Label className="text-sm text-green-700">Reference (optional)</Label>
              <Input
                placeholder="Receipt/EFT reference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                className="bg-white"
                data-testid="payment-reference-input"
              />
            </div>
            <Button
              onClick={handleQuickPayment}
              disabled={processing || !paymentAmount}
              className="bg-green-600 hover:bg-green-700 h-10 px-6"
              data-testid="quick-payment-btn"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay & Auto-Allocate
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-green-600 mt-2">
            Payment will be automatically allocated to oldest unpaid invoices first
          </p>
        </div>

        {/* Unpaid Invoices Table */}
        <div className="flex-1 overflow-auto mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : unpaidInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500 bg-green-50 rounded-lg">
              <CheckCircle className="h-10 w-10 text-green-500 mb-2" />
              <p className="font-medium text-green-700">All invoices are paid!</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-lg font-semibold text-red-700">
                  Unpaid Invoices ({unpaidInvoices.length})
                </Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleExportCSV}>
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-red-50">
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        className="w-4 h-4"
                        checked={unpaidInvoices.every(inv => selectedInvoices[inv.id])}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allSelected = {};
                            unpaidInvoices.forEach(inv => {
                              allSelected[inv.id] = inv.balance;
                            });
                            setSelectedInvoices(allSelected);
                          } else {
                            setSelectedInvoices({});
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Paid</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead className="text-right">Pay Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unpaidInvoices.map((inv) => {
                    const isSelected = !!selectedInvoices[inv.id];
                    return (
                      <TableRow 
                        key={inv.id}
                        className={`cursor-pointer hover:bg-red-50 ${isSelected ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''}`}
                        onClick={() => toggleInvoiceSelection(inv)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={isSelected}
                            onChange={() => toggleInvoiceSelection(inv)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{inv.date}</TableCell>
                        <TableCell className="font-mono">{inv.inv_no || '-'}</TableCell>
                        <TableCell className="text-right">R {Math.abs(inv.inv_total).toFixed(2)}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {inv.inv_amt_paid > 0 ? `R ${inv.inv_amt_paid.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell className="text-right font-bold text-red-600">
                          R {Math.abs(inv.balance).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getPeriodStyle(inv.period)}`}>
                            {inv.period}
                          </span>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {isSelected && (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max={inv.balance}
                              className="w-24 text-right h-8"
                              value={selectedInvoices[inv.id] || ''}
                              onChange={(e) => updateAllocation(inv.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </div>

        {/* Aging Summary */}
        {data && (
          <div className="border-t pt-3 mt-2">
            <Label className="text-sm font-semibold text-gray-700 mb-2 block">Invoice Aging Breakdown</Label>
            <div className="grid grid-cols-5 gap-2 text-center text-sm">
              <div className="p-2 bg-green-50 rounded">
                <div className="text-xs text-green-600">Current</div>
                <div className="font-bold text-green-700">R {Math.abs(data.aging.current).toFixed(2)}</div>
              </div>
              <div className="p-2 bg-yellow-50 rounded">
                <div className="text-xs text-yellow-600">30 Days</div>
                <div className="font-bold">R {Math.abs(data.aging.days_30).toFixed(2)}</div>
              </div>
              <div className="p-2 bg-orange-50 rounded">
                <div className="text-xs text-orange-600">60 Days</div>
                <div className="font-bold">R {Math.abs(data.aging.days_60).toFixed(2)}</div>
              </div>
              <div className="p-2 bg-red-50 rounded">
                <div className="text-xs text-red-500">90 Days</div>
                <div className="font-bold">R {Math.abs(data.aging.days_90).toFixed(2)}</div>
              </div>
              <div className="p-2 bg-red-100 rounded">
                <div className="text-xs text-red-600">120+ Days</div>
                <div className="font-bold text-red-700">R {Math.abs(data.aging.days_120).toFixed(2)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Footer with Selected Payment */}
        <DialogFooter className="border-t pt-4 flex justify-between items-center">
          <div>
            {Object.keys(selectedInvoices).length > 0 && (
              <span className="text-sm text-gray-600">
                Selected: {Object.keys(selectedInvoices).length} invoice(s) = 
                <span className="font-bold text-green-600 ml-1">R {totalSelected.toFixed(2)}</span>
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            {Object.keys(selectedInvoices).length > 0 && (
              <Button 
                onClick={handlePaySelected} 
                disabled={processing || totalSelected <= 0}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="pay-selected-btn"
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Allocate R {totalSelected.toFixed(2)} to Selected
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReconcileModal;
